/**
 * Shared base-query scaffolding for every RTK Query slice that talks
 * to the ontology proxy.
 *
 * `graphApi`, `plantViewerApi`, `agentWritesApi`, and the other ontology
 * slices use the same upstream
 * (`/api/v1/ontology`) and the same auth + branch headers, so they
 * share `ontologyBaseQuery` from this file. Each slice owns its own
 * `createApi` call, `tagTypes`, and `reducerPath` so cache spaces stay
 * isolated; graph-derived cross-slice invalidation is centralized in
 * `api/cache-coherence`.
 *
 * `drainCypherRows` lives here too because every slice that issues
 * paginated `/cypher/read` landing queries needs it; it is fully
 * generic and has no Plant Viewer-specific dependencies.
 */

import { fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type {
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta
} from '@reduxjs/toolkit/query/react'
import { AuthenticationStorage } from '@axilon-apps/domains-identity/shared/core'
import { refreshAuthenticationToken } from '@axilon-apps/domains-identity/state'
import { VIEW_AS_USER_HEADER } from '@atbash-labs/proxy-client'

import { getOntologyApiBaseUrl, getProxyBaseUrl } from '../../config'
import {
  getApiKey,
  getOntologyBranchHeaderValue,
  getOntologySiteHeaderValue
} from './ontology-api-auth'
import { TRACEPARENT_HEADER, nextRequestTraceparent } from './traceparent'

export const ONTOLOGY_BRANCH_HEADER = 'X-Ontology-Branch'
export const ONTOLOGY_SITE_HEADER = 'X-Ontology-Site'
export const ORGANIZATION_HEADER = 'X-Organization-ID'

const ONTOLOGY_API_BASE_URL = getOntologyApiBaseUrl()

export function getAuthenticationOrganizationId(): string | undefined {
  const payload = AuthenticationStorage.accessToken?.getPayload?.()
  const organizationId =
    payload && 'organization_id' in payload ? payload.organization_id : undefined
  return typeof organizationId === 'string' && organizationId.trim()
    ? organizationId.trim()
    : undefined
}

/**
 * Absolute URL for proxy-server resources that live under `/api/v1`
 * directly (custom agents, notifications, documents, …), as opposed to the
 * `/api/v1/ontology` graph pass-through that `ontologyBaseQuery` is rooted at.
 *
 * We use absolute URLs for these endpoints so a slice can mix
 * ontology and proxy-owned resources without RTK Query's
 * `fetchBaseQuery` re-rooting the leading-slash path against
 * `ONTOLOGY_API_BASE_URL` (producing a broken
 * `/api/v1/ontology/api/v1/…`).
 *
 * When `PROXY_BASE_URL` is empty (same-origin dev deploys) we prefix
 * with `window.location.origin` so the resulting URL still starts
 * with `http(s)://`.
 */
function resolveProxyApiV1Url(): string {
  const base = getProxyBaseUrl()
  if (base) return `${base}/api/v1`
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api/v1`
  }
  return '/api/v1'
}

export const PROXY_API_V1_URL = resolveProxyApiV1Url()

const rawOntologyBaseQuery = fetchBaseQuery({
  baseUrl: ONTOLOGY_API_BASE_URL,
  prepareHeaders: (headers) => {
    const apiKey = getApiKey()
    if (apiKey) {
      headers.set('X-API-Key', apiKey)
    }
    // Honor a per-request branch/site override (e.g. cross-branch site
    // inventory, agent-write apply). Only fall back to the active context
    // when the caller did not already set the header.
    const branch = getOntologyBranchHeaderValue()
    if (branch && !headers.has(ONTOLOGY_BRANCH_HEADER)) {
      headers.set(ONTOLOGY_BRANCH_HEADER, branch)
    }
    const site = getOntologySiteHeaderValue()
    if (site && !headers.has(ONTOLOGY_SITE_HEADER)) {
      headers.set(ONTOLOGY_SITE_HEADER, site)
    }
    const organizationId = getAuthenticationOrganizationId()
    if (organizationId) {
      headers.set(ORGANIZATION_HEADER, organizationId)
    }
    headers.set(TRACEPARENT_HEADER, nextRequestTraceparent())
    const token = AuthenticationStorage.accessToken?.token
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    return headers
  }
})

function hasExplicitBranchHeader(headers: FetchArgs['headers']): boolean {
  if (!headers) return false
  if (headers instanceof Headers) return headers.has(ONTOLOGY_BRANCH_HEADER)
  if (Array.isArray(headers)) {
    return headers.some(([name]) => name?.toLowerCase() === ONTOLOGY_BRANCH_HEADER.toLowerCase())
  }
  return Object.keys(headers).some(
    (name) => name.toLowerCase() === ONTOLOGY_BRANCH_HEADER.toLowerCase()
  )
}

/**
 * The base query every ontology slice uses: `rawOntologyBaseQuery` plus
 * identity-token lifecycle handling. Refresh itself is owned by
 * `domains/identity` (`refreshAuthenticationToken`) — this wrapper only
 * decides *when* to invoke it:
 *
 * - proactively, when the stored access token is (about to be) expired,
 *   before the request goes out;
 * - reactively, on a 401 response — one forced refresh, then exactly one
 *   retry (`prepareHeaders` re-reads the freshly persisted token).
 *
 * Unauthenticated contexts stay no-ops: with no access token the proactive
 * check short-circuits, and with no refresh token a 401 is returned as-is.
 */
export const ontologyBaseQuery: typeof rawOntologyBaseQuery = async (args, api, extraOptions) => {
  const activeBranchAtStart = getOntologyBranchHeaderValue()
  const hasBranchOverride = typeof args !== 'string' && hasExplicitBranchHeader(args.headers)
  if (AuthenticationStorage.accessToken?.isExpired()) {
    const refresh = await refreshAuthenticationToken(api.dispatch)
    if (!refresh.ok) {
      return {
        error: { status: 401, data: refresh.error } as FetchBaseQueryError
      }
    }
  }

  const isReadRequest =
    typeof args === 'string' ||
    !args.method ||
    args.method.toUpperCase() === 'GET' ||
    args.method.toUpperCase() === 'HEAD'
  const viewAsUserId = api.getState() as { viewAs?: { target?: { id?: string } | null } }
  const targetUserId = viewAsUserId.viewAs?.target?.id
  const requestArgs =
    isReadRequest && targetUserId
      ? typeof args === 'string'
        ? { url: args, headers: { [VIEW_AS_USER_HEADER]: targetUserId } }
        : {
            ...args,
            headers: {
              ...(args.headers ?? {}),
              [VIEW_AS_USER_HEADER]: targetUserId
            }
          }
      : args

  let result = await rawOntologyBaseQuery(requestArgs, api, extraOptions)

  if (result.error?.status === 401 && AuthenticationStorage.refreshToken) {
    const refresh = await refreshAuthenticationToken(api.dispatch, { force: true })
    if (refresh.ok) {
      result = await rawOntologyBaseQuery(requestArgs, api, extraOptions)
    }
  }

  // A retired active branch prevents even the branch list from loading. Reset
  // only when this request used the persisted selection and the server gave
  // the branch resolver's definitive missing-branch reason (412, so it is never
  // confused with a missing resource). Import lazily to avoid a module
  // initialization cycle through branchesApi.
  if (
    result.error?.status === 412 &&
    result.error.data &&
    typeof result.error.data === 'object' &&
    'reason' in result.error.data &&
    result.error.data.reason === 'BRANCH_NOT_FOUND' &&
    !hasBranchOverride &&
    activeBranchAtStart &&
    getOntologyBranchHeaderValue() === activeBranchAtStart
  ) {
    const { applyBranchSwitch } = await import('../../active-branch')
    if (getOntologyBranchHeaderValue() === activeBranchAtStart) {
      applyBranchSwitch(api.dispatch, undefined)
    }
  }

  return result
}

/** Headers for non-RTK `fetch` calls (agents, connection test, …). */
export function createOntologyHeaders(): Headers {
  const headers = new Headers({
    'Content-Type': 'application/json'
  })

  const apiKey = getApiKey()
  if (apiKey) {
    headers.set('X-API-Key', apiKey)
  }

  const branch = getOntologyBranchHeaderValue()
  if (branch) {
    headers.set(ONTOLOGY_BRANCH_HEADER, branch)
  }

  const site = getOntologySiteHeaderValue()
  if (site) {
    headers.set(ONTOLOGY_SITE_HEADER, site)
  }

  const organizationId = getAuthenticationOrganizationId()
  if (organizationId) {
    headers.set(ORGANIZATION_HEADER, organizationId)
  }

  headers.set(TRACEPARENT_HEADER, nextRequestTraceparent())

  const token = AuthenticationStorage.accessToken?.token
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  return headers
}

// ─── Cypher landing-query drain helper ────────────────────────────

/**
 * Wire shape returned by the proxy's `/cypher/read` route. Owned here
 * (rather than in any domain-specific package) because every drain
 * caller — Plant Viewer landing queries today, plus any future
 * landing query on another slice — needs to be able to type its
 * `transformResponse` against the same shape.
 *
 * For back-compat callers can still import this as
 * `GraphCypherQueryResult` from `state/src/api/types`.
 */
export interface CypherReadResult {
  count: number
  results: Array<Record<string, unknown>>
}

/** Wire shape returned by the proxy's `/cypher/write` route. */
export interface CypherWriteCounters {
  nodesCreated: number
  nodesDeleted: number
  relationshipsCreated: number
  relationshipsDeleted: number
  propertiesSet: number
  labelsAdded: number
  labelsRemoved: number
}

export interface CypherWriteResult {
  count?: number
  results: Array<Record<string, unknown>>
  counters?: CypherWriteCounters
}

/**
 * Wire shapes for the proxy's debug-only maintenance routes
 * (`/maintenance/clear-postgres`, `/maintenance/clear-neo4j`,
 * `/maintenance/clear-historian`, `/maintenance/postgres-query`).
 * Stores are cleared independently; the Neo4j wipe is scoped to the
 * active branch, the Postgres truncate is tenant-scoped under RLS, and
 * historian Timescale deletes the caller's organization_id rows (or
 * reports skipped when HISTORIAN_POSTGRES_* is unset). The query console
 * hits the proxy's own ontology Postgres pool (no client-supplied
 * connection).
 */
export interface ClearPostgresResult {
  tablesTruncated: number
  tables: string[]
}

export interface ClearNeo4jResult {
  nodesDeleted: number
  relationshipsDeleted: number
  branch: string
}

export interface ClearHistorianResult {
  skipped: boolean
  reason?: string
  tablesTruncated: number
  tables: string[]
  rowsDeleted: number
}

export interface PostgresQueryArgs {
  query: string
  /** When true, run as Exec (DML/DDL); when false, require SELECT-only. */
  write?: boolean
}

export interface PostgresQueryResult {
  write: boolean
  columns?: string[]
  rows?: unknown[][]
  rowCount?: number
  truncated?: boolean
  rowsAffected?: number
}

/**
 * Page size for `drainCypherRows`.
 *
 * `/cypher/read` returns one page per call — it no longer silently
 * caps un-`LIMIT`ed queries, but it still does not return unbounded
 * result sets in a single response. A landing query that wants every
 * row therefore has to ask for every row, and a partial one is a real
 * correctness hazard here: a truncated `:Process` set orphans the
 * children of any dropped row, because the client groups by
 * `parentId` and a missing parent becomes a permanent dangling
 * reference.
 *
 * So each landing query ends with an explicit
 * `SKIP toInteger($skip) LIMIT toInteger($pageSize)` tail and loops
 * client-side via `drainCypherRows` until a page comes back short.
 * The per-page limit stays modest so any single round-trip is bounded
 * in proxy + Neo4j memory; the outer loop covers any realistic plant
 * size. 500 matches the existing `LIMIT 500` convention used inside
 * `CALL { ... }` subqueries elsewhere in the codebase.
 */
export const CYPHER_DRAIN_PAGE_SIZE = 500

/**
 * Soft upper bound on round-trips per drain. 200 × 500 = 100 000
 * rows, which is multiple orders of magnitude above any realistic
 * landing-query row count, and guards against an infinite loop if
 * the underlying row set shifts during pagination.
 */
export const CYPHER_DRAIN_MAX_PAGES = 200

export type CypherDrainBaseQuery = (args: FetchArgs) => Promise<{
  data?: unknown
  error?: FetchBaseQueryError
  meta?: FetchBaseQueryMeta
}>

export type CypherDrainResult = {
  data?: CypherReadResult
  error?: FetchBaseQueryError
  meta?: FetchBaseQueryMeta
}

/**
 * Drain a `/cypher/read` query that ends with an explicit
 * `SKIP toInteger($skip) LIMIT toInteger($pageSize)` tail. Calls the
 * proxy repeatedly with increasing `$skip` and concatenates the rows
 * into a single `CypherReadResult`, stopping when the proxy returns
 * fewer rows than requested.
 *
 * The first error from any page aborts the drain and is returned to
 * the caller; partial pages are discarded so the RTK cache never
 * holds a torn / half-populated landing dataset.
 *
 * Returned in the same `{ data | error }` shape as a normal RTK
 * baseQuery call so the caller can return it directly from a
 * `queryFn` and let the endpoint's existing `transformResponse` do
 * the rest.
 */
export async function drainCypherRows(
  baseQuery: CypherDrainBaseQuery,
  query: string,
  baseParams: Record<string, unknown> = {},
  pageSize: number = CYPHER_DRAIN_PAGE_SIZE,
  /** Merged into each page request (e.g. per-branch `X-Ontology-Branch`). */
  requestExtras: Pick<FetchArgs, 'headers'> = {}
): Promise<CypherDrainResult> {
  const aggregated: Array<Record<string, unknown>> = []
  let lastMeta: FetchBaseQueryMeta | undefined

  for (let page = 0; page < CYPHER_DRAIN_MAX_PAGES; page++) {
    const skip = page * pageSize
    const result = await baseQuery({
      url: '/cypher/read',
      method: 'POST',
      body: {
        query,
        params: { ...baseParams, skip, pageSize }
      },
      ...requestExtras
    })
    lastMeta = result.meta
    if (result.error) {
      return { error: result.error, meta: result.meta }
    }
    const payload = (result.data ?? {}) as Partial<CypherReadResult>
    const rows = Array.isArray(payload.results) ? payload.results : []
    aggregated.push(...rows)
    if (rows.length < pageSize) {
      return {
        data: { count: aggregated.length, results: aggregated },
        meta: lastMeta
      }
    }
  }

  return {
    data: { count: aggregated.length, results: aggregated },
    meta: lastMeta
  }
}
